import React, { useRef, useState } from 'react';
import SxProps from 'threads5/themes/sx-props';
import type { ITheme } from 'threads5/themes/theme';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import type { Sys } from 'src/types/contentful-sdk-compat';
import { animated, easings, useSpringValue } from '@react-spring/web';
import { useSwipeable } from 'react-swipeable';

import {
  Box,
  Column,
  Container,
  Row,
  Section,
  Stack,
} from 'src/components-v2/Layout';
import { useDeviceSize } from 'src/hooks';
import { Indicator } from 'src/components-v3/Indicator/Indicator';

const If = ({ condition, children }) => {
  if (Boolean(condition)) {
    return children;
  }
  return null;
};

const AnimatedStack = animated(Stack);

export interface ICardsGrid {
  heading?: React.ReactElement;
  mobileLayout?: 'Stacked' | 'Carousel';
  rows: Array<Array<React.ReactElement>>;
  sx?: SxProps<ITheme>;
  sys?: Sys;
}

const gridMap = {
  2: '1fr 1fr',
  3: '1fr 1fr 1fr',
  4: '1fr 1fr 1fr 1fr',
};
const GAP = 16;
const CARD_WIDTH = 287;

const CardsGrid: React.FC<ICardsGrid> = ({
  heading,
  mobileLayout,
  rows,
  sx,
  sys,
}) => {
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });
  const deviceSize = useDeviceSize({ debounce: 10 });
  const layout =
    deviceSize.isSmall && mobileLayout === 'Carousel' ? 'Carousel' : 'Stacked';

  const x = useSpringValue(0, {
    config: {
      duration: 300,
      easing: easings.easeInQuad,
    },
  });
  const progressRef = useRef(0);
  const childrenCount = rows.flat().length;
  const [activeIndex, setActiveIndex] = useState(0);

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      handleClick(activeIndex + 1);
    },
    onSwipedRight: () => {
      handleClick(activeIndex - 1);
    },
    delta: 10,
    preventScrollOnSwipe: true,
    swipeDuration: 1_000,
    touchEventOptions: { passive: true },
  });

  const handleClick = (index: number) => {
    // we return early when the index is out of bounds
    if (index < 0) {
      return;
    } else if (index > childrenCount - 1) {
      return;
    }

    // we set the active index and update the progress
    const delta = index - activeIndex;
    const progress = (CARD_WIDTH + GAP) * delta;
    progressRef.current -= progress;
    x.start(progressRef.current);
    setActiveIndex(index);
  };

  return (
    <Section
      className='cms-cards-grid'
      sx={{
        ...sx,
        '& h2': {
          mb: '0.8rem',
        },
      }}
      {...sx}
    >
      {heading && heading}
      <Container
        {...inspectorProps({ fieldId: 'internalTitle' })}
        className='cms-cards-grid-container'
      >
        <Row className='cms-cards-grid-row'>
          <Column
            className='cms-cards-grid-column'
            xs={22}
            xsOffset={1}
            lg={20}
            lgOffset={2}
          >
            <If condition={layout === 'Stacked'}>
              {rows.map((row, ridx) => {
                return (
                  <Box
                    key={row?.['key']}
                    className='cms-cards-grid-wrapper'
                    sx={{
                      display: 'grid',
                      gap: { xs: 2, lg: 3 },
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: gridMap[row?.length],
                      },
                      justifyContent: 'center',
                      gridAutoRows: '1fr',
                      mb: { xs: '16px', lg: '24px' },
                    }}
                  >
                    {row.map((card, cidx) => {
                      return (
                        <Box
                          key={card?.['key'] + ridx + cidx}
                          className='cms-cards-grid-box'
                        >
                          {card}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </If>
            <If condition={layout === 'Carousel'}>
              <Stack
                className='cms-cards-grid-carousel-container'
                direction='row'
              >
                <AnimatedStack
                  className='cms-cards-grid-carousel-window'
                  direction='row'
                  gap={GAP / 8}
                  ref={handlers.ref}
                  style={{
                    transform: x.to((val) => {
                      return `translateX(${val}px)`;
                    }),
                  }}
                >
                  {rows.flat().map((card, index) => {
                    return (
                      <Box
                        key={card?.['key'] + index}
                        className='cms-cards-grid-carousel-box'
                        sx={{
                          width: `${CARD_WIDTH}px`,
                          flexShrink: 0,
                        }}
                        onFocus={() => {
                          handleClick(index);
                        }}
                      >
                        {card}
                      </Box>
                    );
                  })}
                </AnimatedStack>
              </Stack>
              <Stack
                justifyContent='center'
                flexDirection='row'
                gap={0.75}
                marginTop={4}
              >
                {rows.flat().map((_, i) => {
                  return (
                    <Indicator
                      key={`indicator-${i}`}
                      isActive={activeIndex === i}
                      index={i}
                      onClick={() => {
                        return handleClick(i);
                      }}
                    />
                  );
                })}
              </Stack>
            </If>
          </Column>
        </Row>
      </Container>
    </Section>
  );
};

export default CardsGrid;
